import React, { useState, useEffect } from 'react'
const Hero: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setIsVisible(true)
  }, [])

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const offset = 80
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
  }

  const mockCars = [
    { year: 2024, model: 'BMW M3 Competition', chassis: 'G80', color: 'Isle of Man Green', mileage: '1,204', price: '$78,995', tags: ['Carbon Buckets', 'Individual Color'] },
    { year: 2023, model: 'BMW M4 CSL', chassis: 'G82', color: 'Frozen Brooklyn Grey', mileage: '3,871', price: '$112,500', tags: ['Carbon Buckets', 'Competition Pkg'] },
    { year: 2024, model: 'BMW M2', chassis: 'G87', color: 'Zandvoort Blue', mileage: '987', price: '$64,900', tags: ['Individual Color'] },
    { year: 2022, model: 'BMW 340i xDrive', chassis: 'F30', color: 'Tanzanite Blue', mileage: '18,320', price: '$42,750', tags: ['Competition Pkg'] },
    { year: 2023, model: 'BMW M5 CS', chassis: 'F90', color: 'Brands Hatch Grey', mileage: '6,410', price: '$129,000', tags: ['Carbon Buckets', 'Individual Color', 'Competition Pkg'] },
  ]

  const tickerItems = [
    'G80 M3 · Isle of Man Green · $78,995',
    'G82 M4 CSL · Frozen Brooklyn Grey · $112,500',
    'G87 M2 · Zandvoort Blue · $64,900',
    'F30 340i · Tanzanite Blue · $42,750',
    'F90 M5 CS · Brands Hatch Grey · $129,000',
    'G80 M3 · Fire Orange · $82,400',
    'G82 M4 · Sao Paulo Yellow · $94,100',
  ]

  return (
    <section className="relative overflow-hidden">
      {/* Background: diagonal speed-line stripes */}
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 60px, rgba(225,29,72,0.3) 60px, rgba(225,29,72,0.3) 62px)',
          }}
        />
        {/* Carbon fiber overlay */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '4px 4px',
          }}
        />
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
      </div>

      {/* Ticker bar */}
      <div className="relative w-full bg-[#0d0d0d] border-b border-white/5 overflow-hidden">
        <div className="flex items-center">
          <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-[#141414] border-r border-white/10 z-10">
            <span className="inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
            <span className="font-mono text-xs text-neutral-400 tracking-tight whitespace-nowrap">LIVE</span>
          </div>
          <div className="overflow-hidden flex-1">
            <div className="animate-marquee whitespace-nowrap flex">
              {[...tickerItems, ...tickerItems].map((item, i) => (
                <span key={i} className="inline-flex items-center font-mono text-xs text-neutral-500 mx-6">
                  <span className="w-1 h-1 rounded-full bg-rose-600/60 mr-3" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main content: asymmetric 7/5 grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-24 pb-16 sm:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">

          {/* Left column — 7 cols */}
          <div className="lg:col-span-7 relative">
            {/* Vertical rose accent line */}
            <div className="hidden lg:block absolute left-0 top-0 w-[3px] h-32 bg-gradient-to-b from-rose-600 to-transparent rounded-full" />

            <div className="lg:pl-6">
              {/* Chassis labels */}
              <div
                className={`flex items-center gap-4 mb-6 transform transition-all duration-500 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                }`}
              >
                {['E Chassis', 'F Chassis', 'G Chassis'].map((label) => (
                  <span
                    key={label}
                    className="font-mono text-xs text-neutral-500 tracking-wider uppercase border-b border-neutral-700 pb-0.5"
                  >
                    {label}
                  </span>
                ))}
              </div>

              {/* Main heading — left aligned */}
              <h1
                className={`text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white text-left mb-6 transform transition-all duration-500 delay-100 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                }`}
                style={{ lineHeight: 1.0, letterSpacing: '-0.04em' }}
              >
                Find rare BMWs<br />
                <span className="text-gradient">nationwide.</span>
              </h1>

              {/* Subtitle — left aligned */}
              <p
                className={`text-lg text-neutral-400 leading-relaxed max-w-lg text-left mb-8 transform transition-all duration-500 delay-200 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                }`}
              >
                BMWFinder scans every dealership nationwide to find BMWs with{' '}
                <strong className="text-white font-semibold">Individual Colors</strong>,{' '}
                <strong className="text-white font-semibold">Bucket Seats</strong>, and{' '}
                <strong className="text-white font-semibold">Competition Packages</strong>.
              </p>

              {/* CTA row: button + price inline */}
              <div
                className={`flex flex-wrap items-center gap-5 mb-10 transform transition-all duration-500 delay-300 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                }`}
              >
                <a href="/browse" className="inline-flex items-center gap-2.5 bg-rose-600 text-white px-8 py-4 rounded-lg text-base font-semibold no-underline hover:bg-rose-700 transition-all hover:-translate-y-0.5 border-none" style={{ boxShadow: '0 4px 12px rgba(225, 29, 72, 0.3)' }}>
                  Browse Inventory
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </a>

                <span className="text-sm text-neutral-400">No credit card required</span>
              </div>

              {/* Stat counters with vertical dividers */}
              <div
                className={`flex flex-wrap items-center gap-0 transform transition-all duration-500 delay-400 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                }`}
              >
                {[
                  { value: '1,000+', label: 'Dealers Scanned' },
                  { value: 'VIN', label: 'Decoded' },
                  { value: '24/7', label: 'Updates' },
                ].map((stat, i) => (
                  <div
                    key={stat.label}
                    className={`flex items-center gap-3 ${i > 0 ? 'border-l border-white/10 pl-5 ml-5' : ''}`}
                  >
                    <span className="font-mono text-lg sm:text-xl font-bold text-white tracking-tight">{stat.value}</span>
                    <span className="text-xs text-neutral-500 uppercase tracking-wider">{stat.label}</span>
                  </div>
                ))}
              </div>

              {/* See how it works link */}
              <button
                onClick={() => scrollToSection('features')}
                className="mt-8 inline-flex items-center gap-2 bg-transparent text-neutral-400 text-sm font-medium hover:text-white transition-colors cursor-pointer border-none p-0"
              >
                See how it works
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Right column — 5 cols: inventory table preview */}
          <div
            className={`lg:col-span-5 transform transition-all duration-700 delay-300 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <div className="relative">
              {/* Rose glow behind */}
              <div className="absolute -inset-8 bg-rose-600/8 rounded-full pointer-events-none" style={{ filter: 'blur(40px)' }} />

              {/* Card with 3D tilt */}
              <div
                className="relative bg-[#141414] rounded-xl border border-white/10 overflow-hidden"
                style={{
                  transform: 'rotate(-1deg)',
                }}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0d0d0d]">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    <span className="font-mono text-xs text-neutral-400">Live Inventory</span>
                  </div>
                  <span className="font-mono text-[10px] text-neutral-600">Updated 2m ago</span>
                </div>

                {/* Condensed card rows */}
                <div className="divide-y divide-white/5">
                  {mockCars.map((car, index) => (
                    <div key={index} className="px-4 py-3 hover:bg-white/[0.02] transition-colors">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded bg-white/5 border border-white/10 flex items-center justify-center text-[10px] font-bold text-rose-500 font-mono">
                            {car.chassis.charAt(0)}
                          </div>
                          <div>
                            <span className="text-sm text-white font-medium">{car.year} {car.model.replace('BMW ', '')}</span>
                            <span className="ml-1.5 font-mono text-[10px] text-neutral-600">{car.chassis}</span>
                          </div>
                        </div>
                        <span className="font-mono text-sm text-white font-semibold">{car.price}</span>
                      </div>
                      <div className="flex items-center justify-between pl-8">
                        <span className="text-xs text-neutral-500">{car.color}</span>
                        <span className="font-mono text-[10px] text-neutral-600">{car.mileage} mi</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5 pl-8">
                        {car.tags.map((tag) => (
                          <span key={tag} className="inline-block px-1.5 py-0.5 rounded text-[9px] font-medium bg-rose-600/10 text-rose-400 border border-rose-600/20">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom fade with CTA */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-32 flex items-end justify-center pb-4"
                  style={{
                    background: 'linear-gradient(to bottom, transparent 0%, #141414 70%, #141414 100%)',
                  }}
                >
                  <a
                    href="/browse"
                    className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white px-5 py-2.5 rounded-lg text-xs font-semibold hover:bg-[#222] transition-colors border border-white/10 cursor-pointer"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                    Browse full inventory
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero
